// src/components/notifications/NotificationBell.jsx - Updated
import React, { useState } from 'react';
import { Bell, X, Check, Trash2, Calendar, Users, Mail, AlertCircle, Shield } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { InvitationModal } from './InvitationModal';

export const NotificationBell = () => {
  const { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    isProvider 
  } = useNotifications();
  
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const navigate = useNavigate();

  const filteredNotifications = notifications.filter(notification => {
    switch (filter) {
      case 'unread':
        return !notification.read;
      case 'invitations':
        return notification.type === 'organizer_invitation' || notification.category === 'invitation';
      case 'provider':
        return notification.notificationType === 'provider';
      default:
        return true;
    }
  });

  const getCategoryIcon = (notification) => {
    if (notification.type === 'organizer_invitation') return <Mail className="h-5 w-5" />;
    if (notification.category?.includes('request')) return <Calendar className="h-5 w-5" />;
    if (notification.category?.includes('booking')) return <Users className="h-5 w-5" />;
    return <Bell className="h-5 w-5" />;
  };

  const getCategoryColor = (notification) => {
    if (notification.type === 'organizer_invitation') return 'text-emerald-600 bg-emerald-100';
    if (notification.priority === 'high') return 'text-red-600 bg-red-100';
    if (notification.notificationType === 'provider') return 'text-blue-600 bg-blue-100';
    return 'text-gray-600 bg-gray-100';
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Handle invitation notifications with modal
    if (notification.type === 'organizer_invitation') {
      setSelectedInvitation(notification);
      setShowInvitationModal(true);
      setIsOpen(false); // Close the notification dropdown
      return;
    }
    
    // Route to other notification types
    if (notification.data?.bookId) {
      navigate(`/book/${notification.data.bookId}`);
    } else if (notification.actions && notification.actions.length > 0) {
      const primaryAction = notification.actions[0];
      if (primaryAction.url) {
        navigate(primaryAction.url);
      }
    }
    
    setIsOpen(false);
  };

  const handleInvitationModalClose = () => {
    setShowInvitationModal(false);
    setSelectedInvitation(null);
  };

  const handleInvitationActionComplete = () => {
    // Notification context will auto-refresh via the listener
    // Just close the modal
    setShowInvitationModal(false);
    setSelectedInvitation(null);
  };

  if (loading) {
    return (
      <div className="relative">
        <div className="p-2 rounded-full hover:bg-gray-100 transition-colors">
          <Bell className="h-6 w-6 text-gray-400 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        {/* Notification Bell */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <Bell className="h-6 w-6 text-gray-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Notification Panel */}
        {isOpen && (
          <div className="absolute right-[-36px] md:right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              
              {/* Filter Tabs */}
              <div className="flex mt-3 space-x-1 overflow-x-auto">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 text-sm rounded-full whitespace-nowrap transition-colors ${
                    filter === 'all' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  All ({notifications.length})
                </button>
                <button
                  onClick={() => setFilter('unread')}
                  className={`px-3 py-1 text-sm rounded-full whitespace-nowrap transition-colors ${
                    filter === 'unread' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Unread ({unreadCount})
                </button>
                <button
                  onClick={() => setFilter('invitations')}
                  className={`px-3 py-1 text-sm rounded-full whitespace-nowrap transition-colors ${
                    filter === 'invitations' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Invitations ({notifications.filter(n => n.type === 'organizer_invitation').length})
                </button>
                {isProvider && (
                  <button
                    onClick={() => setFilter('provider')}
                    className={`px-3 py-1 text-sm rounded-full whitespace-nowrap transition-colors ${
                      filter === 'provider' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Provider ({notifications.filter(n => n.notificationType === 'provider').length})
                  </button>
                )}
              </div>

              {/* Mark All Read */}
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <Check className="h-3 w-3" />
                  Mark all as read
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {filteredNotifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No notifications found</p>
                </div>
              ) : (
                filteredNotifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.read ? 'bg-blue-50/50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-full flex-shrink-0 ${getCategoryColor(notification)}`}>
                        {getCategoryIcon(notification)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-gray-900 text-sm">
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
                          </span>
                          {notification.type === 'organizer_invitation' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                              Action Required
                            </span>
                          )}
                          {notification.notificationType === 'provider' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              <Shield className="h-3 w-3 mr-1" />
                              Provider
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Delete this notification?')) {
                            deleteNotification(notification.id);
                          }
                        }}
                        className="p-1 hover:bg-red-100 rounded text-red-500 flex-shrink-0"
                        title="Delete notification"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Invitation Modal */}
      <InvitationModal
        invitation={selectedInvitation}
        isOpen={showInvitationModal}
        onClose={handleInvitationModalClose}
        onActionComplete={handleInvitationActionComplete}
      />
    </>
  );
};